import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { formatDate, Snapshot } from '../services/learningApi';
import { useApis } from './learning/common';

interface Props {
  open: boolean;
  chainId: string;
  onClose: () => void;
  /** Called after the saved chain changed (restore or undo) so the editor can reload it. */
  onRestored: () => void | Promise<void>;
}

const REASON_LABELS: Record<Snapshot['reason'], string> = {
  auto_save: 'Saved',
  manual: 'Named version',
  before_restore: 'Before restore',
  before_suggestion: 'Before suggestion',
  restore: 'Restored',
};

function changeText(c: Snapshot['changes']): string | null {
  if (!c) return null;
  const parts: string[] = [];
  if (c.added.length) parts.push(`${c.added.length} step${c.added.length > 1 ? 's' : ''} added`);
  if (c.removed.length) parts.push(`${c.removed.length} removed`);
  if (c.changed.length) parts.push(`${c.changed.length} changed`);
  return parts.length ? parts.join(', ') : 'Layout only';
}

export const ChainVersionHistory: React.FC<Props> = ({ open, chainId, onClose, onRestored }) => {
  const apis = useApis();
  const [items, setItems] = useState<Snapshot[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ snap: Snapshot; missing: string[]; changes: string | null } | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [undo, setUndo] = useState<{ snapshotId: string; warnings: string[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await apis.snapshots.list(chainId);
      setItems(res.snapshots);
    } catch (e: any) {
      setError(e.message);
    }
  }, [apis, chainId]);

  useEffect(() => {
    if (open) {
      setItems(null);
      setError(null);
      load();
    }
  }, [open, load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveNamed = () =>
    run(async () => {
      await apis.snapshots.save(chainId, newName.trim());
      setNewName('');
    });

  const askRestore = async (snap: Snapshot) => {
    setBusy(true);
    try {
      const full = await apis.snapshots.get(chainId, snap.id);
      setConfirm({ snap, missing: full.missing_functions, changes: changeText(full.changes_vs_current) });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const restore = (snapshotId: string, isUndo = false) =>
    run(async () => {
      const res = await apis.snapshots.restore(chainId, snapshotId);
      setConfirm(null);
      await onRestored();
      setUndo(isUndo ? null : { snapshotId: res.undo_snapshot_id, warnings: res.warnings });
    });

  return (
    <>
      <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
        <DialogTitle>Version history</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A version is kept every time the chain is saved (quick successive saves are grouped). Named and pinned versions are kept forever; the rest keep
            the latest 30.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Name the current saved version, e.g. “Before price rules”"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && newName.trim() && saveNamed()}
            />
            <Button variant="outlined" startIcon={<BookmarkAddIcon />} disabled={!newName.trim() || busy} onClick={saveNamed} sx={{ flexShrink: 0 }}>
              Save version
            </Button>
          </Stack>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {!items && !error && <CircularProgress size={24} />}
          {items && items.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No versions yet — one is created the next time you save.
            </Typography>
          )}
          <List dense disablePadding>
            {items?.map((s) => (
              <ListItem
                key={s.id}
                divider
                sx={{ alignItems: 'flex-start', pr: 20 }}
                secondaryAction={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {!s.is_current && (
                      <Tooltip title="Restore this version">
                        <span>
                          <IconButton size="small" disabled={busy} onClick={() => askRestore(s)}>
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={s.pinned ? 'Unpin (may be cleaned up later)' : 'Pin (keep forever)'}>
                      <IconButton size="small" disabled={busy} onClick={() => run(() => apis.snapshots.update(chainId, s.id, { pinned: !s.pinned }))}>
                        {s.pinned ? <PushPinIcon fontSize="small" color="primary" /> : <PushPinOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rename">
                      <IconButton size="small" disabled={busy} onClick={() => setRenaming({ id: s.id, name: s.name || '' })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete this version">
                      <IconButton size="small" disabled={busy} onClick={() => run(() => apis.snapshots.remove(chainId, s.id))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography variant="body2" fontWeight={s.name ? 600 : 400}>
                        {s.name || REASON_LABELS[s.reason] || s.reason}
                      </Typography>
                      {s.is_current && <Chip size="small" color="success" label="current" />}
                      {s.name && <Chip size="small" variant="outlined" label={REASON_LABELS[s.reason] || s.reason} />}
                    </Stack>
                  }
                  secondary={
                    <>
                      {formatDate(s.created_at)}
                      {s.created_by ? ` · ${s.created_by}` : ''} · {s.node_count} steps
                      {changeText(s.changes) ? ` · ${changeText(s.changes)}` : ''}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Restore this version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            “{confirm?.snap.name || REASON_LABELS[confirm?.snap.reason || 'auto_save']}” from {formatDate(confirm?.snap.created_at)} becomes the saved chain.
            {confirm?.changes ? ` Compared to now: ${confirm.changes}.` : ''}
          </Typography>
          <Typography variant="body2" paragraph>
            The current version is kept in history, so you can undo this.
          </Typography>
          <Alert severity="warning" sx={{ mb: confirm?.missing.length ? 1 : 0 }}>
            Unsaved edits in the editor will be replaced.
          </Alert>
          {!!confirm?.missing.length && (
            <Alert severity="error">
              {confirm.missing.length} step(s) use functions that no longer exist. They will show as “Unknown” and need replacing before the chain can run.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => confirm && restore(confirm.snap.id)} disabled={busy}>
            Restore
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!renaming} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename version</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" sx={{ mt: 1 }} value={renaming?.name || ''} onChange={(e) => renaming && setRenaming({ ...renaming, name: e.target.value })} />
          <Typography variant="caption" color="text.secondary">
            Named versions are never cleaned up automatically.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const r = renaming;
              setRenaming(null);
              if (r) run(() => apis.snapshots.update(chainId, r.id, { name: r.name, pinned: r.name.trim() ? true : undefined }));
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!undo}
        autoHideDuration={12000}
        onClose={(_, reason) => reason !== 'clickaway' && setUndo(null)}
        message={
          <Box>
            Version restored.
            {undo?.warnings.map((w) => (
              <Typography key={w} variant="caption" sx={{ display: 'block' }}>
                {w}
              </Typography>
            ))}
          </Box>
        }
        action={
          <Button color="secondary" size="small" disabled={busy} onClick={() => undo && restore(undo.snapshotId, true)}>
            Undo
          </Button>
        }
      />
    </>
  );
};

export default ChainVersionHistory;
